import { Button, FormHelperText, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';

import { SuperAdminInvite } from 'src/types/resources/SuperAdminInvite';
import {
  attributesToSubmit,
  initialValues,
  SendInviteAgainFormData,
  validationSchema,
} from 'src/forms/sendInviteAgain';
import useModals from 'src/hooks/useModals';
import useSnackbar from 'src/hooks/useSnackbar';
import { useResendInviteForSuperadminMutation } from 'src/services/Invites';
import Box from 'src/components/Box';
import TypographyNoWrap from 'src/components/TypographyNoWrap';
import clsx from 'src/utils/clsx';

import styles from './styles';

type SendInviteAgainFormProps = {
  invite: SuperAdminInvite;
};

const SendInviteAgainForm: React.FC<SendInviteAgainFormProps> = ({ invite }) => {
  const { hideModal } = useModals();
  const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSnackbar();
  const [resendInvite, { isLoading }] = useResendInviteForSuperadminMutation();

  const handleSubmit = async (values: SendInviteAgainFormData) => {
    const { message } = attributesToSubmit(values);
    try {
      await resendInvite({ id: invite.id, message }).unwrap();
      enqueueSuccessSnackbar('The invite was sent again through HubSpot');
      hideModal();
    } catch {
      enqueueErrorSnackbar('Failed to send the invite again');
    }
  };

  const {
    values,
    errors,
    handleChange,
    handleSubmit: handleFormikSubmit,
  } = useFormik<SendInviteAgainFormData>({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
  });

  return (
    <form noValidate onSubmit={handleFormikSubmit}>
      <Box sx={styles.wrapper}>
        <Typography variant="h2" sx={styles.title}>
          Send Invite Again
        </Typography>
        <Typography variant="body1" sx={styles.text}>
          Write a new message for this invitation.
        </Typography>
        <Box sx={styles.identifier}>
          <Typography component="span" sx={styles.identifierText}>
            {[invite.firstName, invite.lastName].filter(Boolean).join(' ')}
          </Typography>
          <Typography component="span" sx={styles.identifierSeparator}>
            •
          </Typography>
          <Typography component="span" sx={styles.identifierText}>
            Invite #{invite.id}
          </Typography>
          <Typography component="span" sx={styles.identifierSeparator}>
            •
          </Typography>
          <TypographyNoWrap component="span" tooltipVariant="light" sx={styles.identifierEmail}>
            {invite.email}
          </TypographyNoWrap>
        </Box>
        <TextField
          fullWidth
          id="message"
          name="message"
          label="Message"
          multiline
          required
          rows={4}
          value={values.message}
          onChange={handleChange}
          placeholder="What message would you like to send with this invitation?"
          error={Boolean(errors.message)}
        />
        {errors.message && <FormHelperText error>{errors.message}</FormHelperText>}
        <Box sx={styles.buttons}>
          <Button type="submit" variant="contained" sx={styles.button} disabled={isLoading}>
            Send Invite Again
          </Button>
          <Button
            variant="outlined"
            sx={clsx(styles.button, [[styles.cancelButton, true]])}
            onClick={hideModal}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default SendInviteAgainForm;
